/**
 * Login
 */
import {
  Box,
  Button,
  Container,
  CssBaseline,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useAsyncFn } from "react-use";
import {
  HidePasswordIcon,
  PasswordIcon,
  ShowPasswordIcon,
} from "../components/CustomIcons";
import check from "../images/check.png";
import logo from "../images/mobile-phone.png";

const useStyles = makeStyles((theme) => ({
  insidePaper: {
    marginTop: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  main: {
    marginTop: theme.spacing(7),
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: "100%", // Fix IE 11 issue.
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
    backgroundColor: "#5046B2",
    height: 50,
    fontWeight: "bold",
  },
  paper: {
    display: "flex",
    flexWrap: "wrap",
    "& > *": {
      margin: theme.spacing(1),
      padding: theme.spacing(4),
    },
  },
  iconContainer: {
    display: "flex",
    width: 63,
    height: 63,
    background: "#E5DBFF",
    borderRadius: "50%",
    alignItems: "center",
    justifyContent: "center",
  },
  iconContainerOuter: {
    display: "flex",
    width: 96,
    height: 96,
    background: "#F3F0FF",
    borderRadius: "50%",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 38,
  },
}));

export default function ResetPassword() {
  const classes = useStyles();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { t } = useTranslation();
  const history = useHistory();

  const { email, token } = useParams();

  const [state, postResetPassword] = useAsyncFn(async () => {
    const response = await axios.post("/auth/reset-password", {
      password,
      confirmPassword,
      token: decodeURIComponent(token),
      email,
    });
    return response.data;
  }, [token, email, password, confirmPassword]);

  const submitResetPassword = async () => {
    if (password !== confirmPassword) {
      setErrorMessage("The passwords do not match");
      return;
    }
    const passwordRegex =
      /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{6,}$/;

    if (!passwordRegex.test(password)) {
      setErrorMessage(
        "Invalid password. Password should meet the specified pattern."
      );
      return;
    }
    if (!passwordRegex.test(confirmPassword)) {
      setErrorMessage(
        "Invalid confirm password.The password should meet the specified pattern."
      );
      return;
    }

    postResetPassword()
  };

  useEffect(() => {
    if (state?.value?.success) {
      history.push("/");
      return;
    }
    if (state?.error) {
      if (state?.error?.response?.data?.error) {
        setErrorMessage(state?.error?.response?.data?.error);
        return;
      }
      setErrorMessage(t("Internal error"));
    }
  }, [state, setErrorMessage, t, history]);

  const handleTogglePassword = () => setShowPassword((prev) => !prev);

  const handleToggleConfirmPassword = () =>
    setShowConfirmPassword((prev) => !prev);

  return (
    <>
      <Container
        component="main"
        maxWidth="xs"
        className={clsx(classes.main, "form-style")}
      >
        <CssBaseline />
        <Box
          maxWidth="xs"
          display="flex"
          alignItems="center"
          justifyContent="center"
          marginBottom="50px"
        >
          <img src={logo} alt="flyins" width="100" height="100" />
          <Grid item xs={12}>
            <Typography
              color="primary"
              style={{
                fontSize: "4rem",
                paddingLeft: "25px",
                fontFamily: "Carter One, cursive",
              }}
            >
              Flyins
            </Typography>
          </Grid>
        </Box>

        <div className={classes.paper}>
          <Paper elevation={2}>
            <div className={classes.insidePaper}>
              <form
                className={classes.form}
                onSubmit={(e) => {
                  e.preventDefault();
                  setErrorMessage("");
                  submitResetPassword();
                }}
              >
                {!state?.loading && state?.value?.success ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: "50px",
                      textAlign: "center",
                    }}
                  >
                    <img
                      style={{ marginBottom: 38 }}
                      src={check}
                      alt="reset password success"
                    />
                    <Typography
                      component="h1"
                      variant="h4"
                      style={{ marginBottom: 38 }}
                    >
                      {t("Password reset")}
                    </Typography>
                    <Typography>{t("Password reset text")}</Typography>
                    <Button
                      style={{ fontWeight: "bold", marginTop: 38 }}
                      type="button"
                      fullWidth
                      variant="contained"
                      color="primary"
                      id="submit"
                      className={classes.submit}
                      onClick={() => history.push("/")}
                    >
                      {t("Continue")}
                    </Button>
                  </div>
                ) : (
                  <>
                    <Typography
                      component="h1"
                      variant="h4"
                      style={{ marginBottom: 10, fontWeight: 500 }}
                    >
                      {t("Set new password")}
                    </Typography>
                    <Typography
                      component="p"
                      variant="caption"
                      style={{
                        marginTop: 10,
                        marginBottom: 44,
                        fontSize: 14,
                        color: "#495057",
                      }}
                    >
                      Your new password must be different to previously used
                      passwords
                    </Typography>
                    {errorMessage && (
                      <Typography
                        style={{
                          color: "red",
                          marginBottom: 5,
                          textAlign: "center",
                        }}
                      >
                        {errorMessage}
                      </Typography>
                    )}
                    <TextField
                      variant="outlined"
                      margin="normal"
                      required
                      fullWidth
                      name="password"
                      label={t("Password")}
                      type={showPassword ? "text" : "password"}
                      id="password"
                      value={password}
                      autoComplete="current-password"
                      InputProps={{
                        minLength: "6",
                        startAdornment: (
                          <InputAdornment position="start">
                            <PasswordIcon
                              className={classes.icon}
                              style={{ marginTop: 10 }}
                            />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleTogglePassword}
                              edge="end"
                            >
                              {showPassword ? (
                                <ShowPasswordIcon
                                  className={classes.icon}
                                  style={{ marginTop: 10 }}
                                />
                              ) : (
                                <HidePasswordIcon
                                  className={classes.icon}
                                  style={{ marginTop: 10 }}
                                />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <Typography
                      component="p"
                      variant="caption"
                      style={{
                        marginTop: 8,
                        marginBottom: 30,
                        fontSize: 14,
                        color: "#495057",
                      }}
                    >
                      Minimum of 6 characters. Contains an uppercase letter,
                      numbers and special characters.
                    </Typography>
                    <TextField
                      variant="outlined"
                      margin="normal"
                      required
                      fullWidth
                      name="confirmpassword"
                      label={t("Confirm Password")}
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmpassword"
                      value={confirmPassword}
                      autoComplete="current-password"
                      InputProps={{
                        minLength: "6",
                        startAdornment: (
                          <InputAdornment position="start">
                            <PasswordIcon
                              className={classes.icon}
                              style={{ marginTop: 10 }}
                            />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleToggleConfirmPassword}
                              edge="end"
                            >
                              {showConfirmPassword ? (
                                <ShowPasswordIcon
                                  className={classes.icon}
                                  style={{ marginTop: 10 }}
                                />
                              ) : (
                                <HidePasswordIcon
                                  className={classes.icon}
                                  style={{ marginTop: 10 }}
                                />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                    <Button
                      disabled={state.loading}
                      type="submit"
                      fullWidth
                      variant="contained"
                      color="primary"
                      id="submit"
                      className={classes.submit}
                    >
                      {state.loading
                        ? t("Resetting Password")
                        : t("Reset Password")}
                    </Button>
                  </>
                )}
              </form>
            </div>
          </Paper>
        </div>
      </Container>
    </>
  );
}
